'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { Card, CardContent } from '@/components/ui/card';
import { 
    FileText, 
    Sparkles, 
    Shield, 
    Clock, 
    CheckCircle,
    UserCheck,
    Stethoscope,
    Paintbrush,
    Eye,
    Timer
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function WhiteningProcessSection() {
    const { t, isLoading } = useTranslation('teeth-whitening');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isLoading) return;

        const ctx = gsap.context(() => {
            // Title animation
            if (titleRef.current) {
                gsap.fromTo(titleRef.current,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: titleRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Timeline animation
            if (timelineRef.current?.children) {
                const steps = Array.from(timelineRef.current.children);
                gsap.fromTo(steps,
                    { opacity: 0, x: isRTL ? 50 : -50 },
                    {
                        opacity: 1,
                        x: 0,
                        duration: 0.8,
                        stagger: 0.2,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: timelineRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

        }, sectionRef);

        return () => ctx.revert();
    }, [isLoading, isRTL]);

    const processSteps = [
        {
            icon: FileText,
            step: "1",
            title: t('whiteningProcess.steps.consultation.title'),
            description: t('whiteningProcess.steps.consultation.description'),
            duration: t('whiteningProcess.steps.consultation.duration'),
            color: "from-blue-500 to-blue-600"
        },
        {
            icon: UserCheck,
            step: "2", 
            title: t('whiteningProcess.steps.examination.title'),
            description: t('whiteningProcess.steps.examination.description'),
            duration: t('whiteningProcess.steps.examination.duration'),
            color: "from-green-500 to-green-600"
        },
        {
            icon: Shield,
            step: "3",
            title: t('whiteningProcess.steps.preparation.title'),
            description: t('whiteningProcess.steps.preparation.description'),
            duration: t('whiteningProcess.steps.preparation.duration'),
            color: "from-purple-500 to-purple-600"
        },
        {
            icon: Paintbrush,
            step: "4",
            title: t('whiteningProcess.steps.application.title'),
            description: t('whiteningProcess.steps.application.description'),
            duration: t('whiteningProcess.steps.application.duration'),
            color: "from-orange-500 to-orange-600"
        },
        {
            icon: Timer,
            step: "5",
            title: t('whiteningProcess.steps.activation.title'),
            description: t('whiteningProcess.steps.activation.description'),
            duration: t('whiteningProcess.steps.activation.duration'),
            color: "from-red-500 to-red-600"
        },
        {
            icon: Eye,
            step: "6",
            title: t('whiteningProcess.steps.evaluation.title'),
            description: t('whiteningProcess.steps.evaluation.description'),
            duration: t('whiteningProcess.steps.evaluation.duration'),
            color: "from-gold to-gold-600"
        }
    ];

    if (isLoading) {
        return (
            <section className="py-16 lg:py-24 bg-white dark:bg-slate-800">
                <div className="container">
                    <div className="text-center mb-16 animate-pulse">
                        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-6 max-w-4xl mx-auto"></div>
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded max-w-3xl mx-auto"></div>
                    </div>
                    <div className="relative max-w-5xl mx-auto">
                        <div className="space-y-12">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="flex items-center gap-8 animate-pulse">
                                    <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                                    <div className="flex-1">
                                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-3 w-48"></div>
                                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="process" ref={sectionRef} className="py-16 lg:py-24 bg-white dark:bg-slate-800">
            <div className="container">
                <div ref={titleRef} className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {t('whiteningProcess.title.part1')} <span className="text-gold">{t('whiteningProcess.title.part2')}</span>
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t('whiteningProcess.subtitle')}
                    </p>
                </div>

                <div ref={timelineRef} className="relative max-w-5xl mx-auto">
                    {/* Connecting Line */}
                    <div className={`absolute ${isRTL ? 'right-10' : 'left-10'} top-10 bottom-10 w-px bg-gradient-to-b from-gold via-gold-600 to-gold opacity-30`}></div>

                    <div className="space-y-12">
                        {processSteps.map((step, index) => (
                            <div key={index} className={`relative flex items-center gap-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                {/* Step Number Circle */}
                                <div className="relative z-10 flex-shrink-0">
                                    <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                                        <step.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gold rounded-full flex items-center justify-center text-white font-bold text-sm">
                                        {step.step}
                                    </div>
                                </div>

                                {/* Step Content */}
                                <Card className="flex-1 border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all duration-300 hover:border-gold/50">
                                    <CardContent className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <h3 className="text-xl font-bold text-navy dark:text-white">
                                                {step.title}
                                            </h3>
                                            <div className="flex items-center gap-1 text-gold bg-gold/10 px-3 py-1 rounded-full">
                                                <Clock className="w-4 h-4" />
                                                <span className="text-sm font-medium">{step.duration}</span>
                                            </div>
                                        </div>
                                        <p className={`text-slate-600 dark:text-slate-300 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                                            {step.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Summary Card */}
                <div className="max-w-4xl mx-auto mt-16">
                    <Card className="bg-gold/10 border-gold/30">
                        <CardContent className="p-8 text-center">
                            <div className={`flex items-center justify-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Sparkles className="w-8 h-8 text-gold" />
                                <h3 className="text-2xl font-bold text-navy dark:text-white">
                                    {t('whiteningProcess.summary.title')}
                                </h3>
                            </div>

                            <div className="grid md:grid-cols-3 gap-6 mb-6">
                                <div className={`${isRTL ? 'text-right' : 'text-center'}`}>
                                    <Clock className="w-8 h-8 text-gold mx-auto mb-2" />
                                    <div className="text-lg font-bold text-navy dark:text-white">{t('whiteningProcess.summary.totalTime')}</div>
                                    <div className="text-sm text-slate-600 dark:text-slate-300">{t('whiteningProcess.summary.appointment')}</div>
                                </div>
                                
                                <div className={`${isRTL ? 'text-right' : 'text-center'}`}>
                                    <Sparkles className="w-8 h-8 text-gold mx-auto mb-2" />
                                    <div className="text-lg font-bold text-navy dark:text-white">{t('whiteningProcess.summary.results')}</div>
                                    <div className="text-sm text-slate-600 dark:text-slate-300">{t('whiteningProcess.summary.whitening')}</div>
                                </div>
                                
                                <div className={`${isRTL ? 'text-right' : 'text-center'}`}>
                                    <Shield className="w-8 h-8 text-gold mx-auto mb-2" />
                                    <div className="text-lg font-bold text-navy dark:text-white">{t('whiteningProcess.summary.comfort')}</div>
                                    <div className="text-sm text-slate-600 dark:text-slate-300">{t('whiteningProcess.summary.experience')}</div>
                                </div>
                            </div>

                            <p className={`text-slate-600 dark:text-slate-300 leading-relaxed ${isRTL ? 'text-right' : 'text-center'}`}>
                                {t('whiteningProcess.summary.description')}
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </section>
    );
}